import { PlayerRef } from '@remotion/player';
import { useEffect, useState, useRef, useCallback } from 'react';

export const useCurrentPlayerFrame = (ref: React.RefObject<PlayerRef | null>) => {
  const [frame, setFrame] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const lastPlayerRef = useRef<PlayerRef | null>(null);
  const lastFrameRef = useRef<number>(0);

  // Memoized function to update frame
  const updateFrame = useCallback(() => {
    if (lastPlayerRef.current) {
      const currentFrame = lastPlayerRef.current.getCurrentFrame();
      if (currentFrame !== lastFrameRef.current) {
        setFrame(currentFrame);
        lastFrameRef.current = currentFrame;
      }
    }
    if (lastPlayerRef.current?.isPlaying()) {
      animationFrameRef.current = requestAnimationFrame(updateFrame);
    }
  }, []);

  useEffect(() => {
    const player = ref.current;
    lastPlayerRef.current = player;

    if (!player) {
      console.warn('Player ref is null, no updates started');
      return;
    }

    // Try event-based updates with timeupdate
    const onTimeUpdate = (e: CustomEvent) => {
      const currentFrame = e.detail.frame;
      if (currentFrame !== lastFrameRef.current) {
        setFrame(currentFrame);
        lastFrameRef.current = currentFrame;
      }
    };

    // Fallback to requestAnimationFrame if not playing or no events
    const startFrameUpdates = () => {
      if (!animationFrameRef.current && player.isPlaying()) {
        animationFrameRef.current = requestAnimationFrame(updateFrame);
      }
    };

    const stopFrameUpdates = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };

    const onPlay = () => {
      startFrameUpdates();
    };

    const onPause = () => {
      stopFrameUpdates();
      if (lastPlayerRef.current) {
        const currentFrame = lastPlayerRef.current.getCurrentFrame();
        setFrame(currentFrame);
        lastFrameRef.current = currentFrame;
      }
    };

    const onSeeked = () => {
      if (lastPlayerRef.current) {
        const currentFrame = lastPlayerRef.current.getCurrentFrame();
        setFrame(currentFrame);
        lastFrameRef.current = currentFrame;
      }
    };

    player.addEventListener('timeupdate', onTimeUpdate);
    player.addEventListener('play', onPlay);
    player.addEventListener('pause', onPause);
    player.addEventListener('seeked', onSeeked);

    // Initialize frame and start updates if playing
    const initialFrame = player.getCurrentFrame();
    setFrame(initialFrame);
    lastFrameRef.current = initialFrame;
    if (player.isPlaying()) {
      startFrameUpdates();
    }

    return () => {
      stopFrameUpdates();
      player.removeEventListener('timeupdate', onTimeUpdate);
      player.removeEventListener('play', onPlay);
      player.removeEventListener('pause', onPause);
      player.removeEventListener('seeked', onSeeked);
    };
  }, [ref.current, updateFrame]);

  return frame;
};